/**
 * Auth Broadcast
 * Multi-tab synchronization for auth events using BroadcastChannel
 * Follows the pattern from lib/artifacts/sync.ts
 */ const AUTH_CHANNEL_NAME = 'auth-sync';
/**
 * Creates a BroadcastChannel for auth synchronization
 * Returns null if BroadcastChannel is not supported
 */ export function createAuthChannel() {
    if (typeof BroadcastChannel === 'undefined') {
        console.warn('BroadcastChannel not supported in this environment');
        return null;
    }
    try {
        return new BroadcastChannel(AUTH_CHANNEL_NAME);
    } catch (error) {
        console.error('Failed to create auth BroadcastChannel:', error);
        return null;
    }
}
/**
 * Broadcasts a login event to other tabs
 */ export function broadcastLogin(channel, expiresIn) {
    if (!channel) return;
    const event = {
        type: 'login',
        timestamp: Date.now(),
        expiresIn
    };
    try {
        channel.postMessage(event);
    } catch (error) {
        console.error('Failed to broadcast login:', error);
    }
}
/**
 * Broadcasts a logout event to other tabs
 */ export function broadcastLogout(channel) {
    if (!channel) return;
    const event = {
        type: 'logout',
        timestamp: Date.now()
    };
    try {
        channel.postMessage(event);
    } catch (error) {
        console.error('Failed to broadcast logout:', error);
    }
}
/**
 * Broadcasts a token refresh event to other tabs
 */ export function broadcastRefresh(channel, expiresIn) {
    if (!channel) return;
    const event = {
        type: 'refresh',
        timestamp: Date.now(),
        expiresIn
    };
    try {
        channel.postMessage(event);
    } catch (error) {
        console.error('Failed to broadcast refresh:', error);
    }
}
/**
 * Sets up a listener for auth sync events
 * Returns a cleanup function to remove the listener
 */ export function setupAuthListener(channel, callbacks) {
    if (!channel) return null;
    const handler = (event)=>{
        const { type, expiresIn } = event.data;
        switch(type){
            case 'login':
                callbacks.onLogin?.(expiresIn);
                break;
            case 'logout':
                callbacks.onLogout?.();
                break;
            case 'refresh':
                if (expiresIn !== undefined) {
                    callbacks.onRefresh?.(expiresIn);
                }
                break;
        }
    };
    channel.addEventListener('message', handler);
    return ()=>{
        channel.removeEventListener('message', handler);
    };
}
/**
 * Closes the auth channel
 */ export function closeAuthChannel(channel) {
    if (channel) {
        try {
            channel.close();
        } catch (error) {
            console.error('Failed to close auth channel:', error);
        }
    }
}
